import ListItem from 'components/generic/ListItem'
import DotsMenuSupport from './DotsMenuSupport'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import dayjs from 'dayjs'
import { Form, RiskAssessment } from '@lmc/ts-common'
import {
  CarePlansPopup,
  RiskAssessmentExtended
} from 'components/carePlans/CarePlansPopup'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CarePlanNextReviewDate } from 'components/carePlans'

export type CarePlanItemBaseProps = {
  /**
   * Here is the CarePlan ID, not CarePlanAdl ID
   */
  id: string
  title: string | undefined
  linkedDateTime: string
  /**
   * the actual name of the carer, not the ID
   */
  linkedBy: string
  riskAssesmentsIDs: string[]
  allRiskAssesments: RiskAssessment[]
  icon: React.ReactChild
  iconColor: string | undefined
  isLoading: boolean
  nextReviewDate: string | undefined
}

interface CarePlanItemProps extends CarePlanItemBaseProps {
  onUnlink: (id: string) => void
  disabled: boolean
  testId?: string
}

const CarePlanItem = ({
  icon,
  iconColor,
  title = '',
  onUnlink,
  id,
  riskAssesmentsIDs = [],
  linkedDateTime,
  linkedBy,
  allRiskAssesments = [],
  isLoading,
  disabled,
  nextReviewDate,
  testId
}: CarePlanItemProps) => {
  const { t } = useTranslation(NAMESPACE.GOALS)
  const baseId = testId || 'care-plan-item'
  const subLabel = `${t(goalsConstants.LINKED)} ${dayjs(linkedDateTime).format(
    'DD-MM-YYYY - HH:mm'
  )} - ${linkedBy}`

  const riskAssesmentsCount = riskAssesmentsIDs.length
  const filteredRiskAssesments = useMemo(
    () => filterRiskAssesments(riskAssesmentsIDs, allRiskAssesments),
    [allRiskAssesments, riskAssesmentsIDs]
  )
  const { id: residentID } = useParams()
  const navigate = useNavigate()

  const redirectToRisk = () => {
    navigate(`/residents/${residentID}/care-plans/${id}`)
  }

  return (
    <ListItem
      hasBorder
      styleHeader={{
        alignItems: 'center'
      }}
      icon={icon}
      iconColor={iconColor}
      label={title}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSelect={disabled ? redirectToRisk : undefined}
      subLabel={subLabel}
      multiSelect
      subLabelChildren={
        riskAssesmentsCount && !isLoading ? (
          <CarePlansPopup
            hidePopover={!disabled}
            riskAssessmentCarePlans={[]}
            carePlanRiskAssessments={filteredRiskAssesments}
          />
        ) : null
      }
      data-testid={baseId}
    >
      {disabled ? (
        // @ts-ignore
        <CarePlanNextReviewDate
          date={nextReviewDate}
          style={{
            paddingRight: 5,
            alignSelf: 'center'
          }}
          data-testid={`${baseId}-next-review`}
        />
      ) : (
        <DotsMenuSupport
          onUnlink={() => onUnlink(id)}
          testId={`${baseId}-dots`}
        />
      )}
    </ListItem>
  )
}

export const filterRiskAssesments = (
  riskAssesmentsIDs: string[],
  allRiskAssesments: RiskAssessment[]
): RiskAssessmentExtended[] => {
  const requiredRisks = riskAssesmentsIDs.map((riskID) =>
    allRiskAssesments.find((riskAssesment) => riskAssesment.id === riskID)
  )

  const filteredRisks = requiredRisks.filter(
    (risk) => risk !== undefined
  ) as Required<typeof requiredRisks>

  const extendedRisk = filteredRisks.map((risk) => {
    const form = risk.form as Form

    return {
      name: form.name,
      icon: form.icon,
      form,
      ...risk
    }
  })

  return extendedRisk as any[]
}

export default CarePlanItem
